import * as Api from '@ViewModels';
import { css } from 'aphrodite';
import { useEffect, useRef, useState } from 'react';
import { toast } from 'sonner';
import { useAdminUserSession } from '../../../../../models/hooks/adminStateHooks';
import { isEmailProviderConfigured } from '../../../../../admin/utils';
import { isValidDomain } from '../../../../../models/utils/validationUtils';
import { invalidateAccountAdminQuery } from '../../../../../queries/Admin/Account/useAdminAccountQuery';
import { useUpdateAdminAccountMutation } from '../../../../../queries/Admin/Account/useUpdateAdminAccountMutation';
import { useUpdateSendEmailFeaturesMutation } from '../../../../../queries/Admin/Account/useUpdateSendEmailFeaturesMutation';
import { AdminPermission } from '../../../../../viewmodels/AdminViewModels';
import { Button } from '../../../../../web/components-v2/Button';
import { Checkbox } from '../../../../../web/components-v2/Checkbox';
import { Dialog, DialogBody, DialogFooter, DialogPopup, DialogTitle } from '../../../../../web/components-v2/Dialog';
import { LoadingSpinner } from '../../../../../web/components/LoadingSpinner';
import { InfoIcon } from '../../../../../web/components/svgs/icons/InfoIcon';
import { BusinessHoursDialog } from './BusinessHoursDialog';
import { EmailConfigurationRow } from './EmailConfigurationRow';
import { emailConfigurationsDialogStyles as s } from './emailConfigurationsDialogStyles';
import { HOLIDAY_ROWS, type IEmailConfigDraft, STANDARD_HOLIDAYS } from './emailConfigTypes';
import { EmailDomainRequestDialog } from './EmailDomainRequestDialog';

export interface IEmailConfigurationsDialogProps {
	account: Api.IAccount;
	open: boolean;
	onClose: () => void;
}

/** Sentinel `emailDomain` value the backend uses when an account sends from multiple domains. */
const NONE_DOMAIN = '@none.com';

const HOLIDAY_INFO = (withTexts: boolean) =>
	`When enabled, ${
		withTexts ? 'email and text campaigns' : 'email campaigns'
	} will automatically pause on the holidays checked below. “Independence Day” pauses on July 4 itself; “Independence Day (Observed)” pauses on the weekday it’s observed when July 4 falls on a weekend.`;

// Compare as sets so a duplicate in a stored list can't make the save gate think nothing changed and
// silently drop an admin's edits.
const holidaySetsEqual = (a: Api.HolidayId[], b: Api.HolidayId[]) => {
	const setA = new Set(a);
	const setB = new Set(b);
	return setA.size === setB.size && [...setA].every(holiday => setB.has(holiday));
};

const EMAIL_DOMAIN_INFO =
	"Configure a dedicated email domain for sending emails from Levitate's servers. This allows accounts with consumer email providers (Gmail, Yahoo, etc.) to send group emails.";

/** A fresh blank configuration row, matching the legacy `onAdd` scaffolding. */
const blankConfig = (key: string): IEmailConfigDraft => ({
	key,
	domain: '',
	exchange: { webServicesUrl: '' },
	imap: { imapHost: '', imapPort: 993, imapUseSsl: true, smtpHost: '', smtpPort: 465, smtpUseSsl: true },
});

/**
 * Account Overview "Configure Email" dialog — a MobX-free rebuild of the legacy
 * `EmailConfigurations` modal (no `AccountOperationsViewModel`). Edits the account's email-provider
 * configurations (domain / provider / send limits / Exchange / IMAP) plus the send-email features
 * (business hours, holiday pausing and which holidays are observed) and the dedicated-domain request
 * flow. Persists via
 * `useUpdateAdminAccountMutation` (configs + derived `emailDomain`) and
 * `useUpdateSendEmailFeaturesMutation` (send-email features), invalidating the account query so the
 * read view refetches.
 */
export const EmailConfigurationsDialog = ({ account, open, onClose }: IEmailConfigurationsDialogProps) => {
	const canSupportLevel3 = useAdminUserSession().hasPermission(AdminPermission.SupportLevel3);

	const [configs, setConfigs] = useState<Record<string, IEmailConfigDraft>>({});
	const [count, setCount] = useState(0);
	const [forceMultipleDomains, setForceMultipleDomains] = useState(false);
	const [isBusinessHoursEnabled, setIsBusinessHoursEnabled] = useState(false);
	const [isObserveHolidaysEnabled, setIsObserveHolidaysEnabled] = useState(true);
	const [observedHolidays, setObservedHolidays] = useState<Api.HolidayId[]>(STANDARD_HOLIDAYS);
	const [businessHoursOpen, setBusinessHoursOpen] = useState(false);
	const [emailDomainOpen, setEmailDomainOpen] = useState(false);

	const updateSendEmailFeatures = useUpdateSendEmailFeaturesMutation({
		onError: () => toast.error('Failed to update send-email settings. Please try again.'),
	});
	const updateAccount = useUpdateAdminAccountMutation();
	const isSaving = updateAccount.isPending || updateSendEmailFeatures.isPending;

	// Re-seed the form from the account only on the closed→open transition, so an account refetch
	// (e.g. after saving business hours) never wipes in-progress edits.
	const wasOpen = useRef(false);
	useEffect(() => {
		if (open && !wasOpen.current) {
			const seeded: Record<string, IEmailConfigDraft> = {};
			let next = 0;
			(account.emailProviderConfigurations ?? []).forEach(config => {
				const key = String(next++);
				seeded[key] = {
					...config,
					key,
					exchange: config.exchange ?? { webServicesUrl: '' },
					imap: config.imap ?? {
						imapHost: '',
						imapPort: 993,
						imapUseSsl: true,
						smtpHost: '',
						smtpPort: 465,
						smtpUseSsl: true,
					},
				};
			});
			setConfigs(seeded);
			setCount(next);
			setForceMultipleDomains(account.emailDomain === NONE_DOMAIN);
			const sendEmail = account.features?.sendEmail;
			setIsBusinessHoursEnabled(!!sendEmail?.observeSendIntervals);
			setIsObserveHolidaysEnabled(sendEmail?.observeHolidays ?? true);
			// A null/absent list means "observe the standard set", mirroring the API's null → Default read.
			setObservedHolidays(sendEmail?.observedHolidays ?? STANDARD_HOLIDAYS);
		}
		wasOpen.current = open;
	}, [open, account]);

	const handleRowChange = (key: string, next: IEmailConfigDraft) =>
		setConfigs(current => ({ ...current, [key]: next }));

	const handleToggleHoliday = (holiday: Api.HolidayId, checked: boolean) =>
		setObservedHolidays(current => {
			if (!checked) {
				return current.filter(observed => observed !== holiday);
			}
			return current.includes(holiday) ? current : [...current, holiday];
		});

	const handleAdd = () => {
		const key = String(count);
		setCount(current => current + 1);
		setConfigs(current => ({ ...current, [key]: blankConfig(key) }));
	};

	const handleDelete = (key: string) => {
		setConfigs(current => {
			const { [key]: _removed, ...rest } = current;
			return rest;
		});
	};

	const persistSendEmailFeatures = async (partial: Partial<Api.ISendEmailFeatures>) => {
		const merged: Partial<Api.ISendEmailFeatures> = { ...(account.features?.sendEmail ?? {}), ...partial };
		await updateSendEmailFeatures.mutateAsync({ accountId: account.id, sendEmailFeatures: merged });
		invalidateAccountAdminQuery();
	};

	const handleBusinessHoursSave = async (partial: Partial<Api.ISendEmailFeatures>) => {
		await persistSendEmailFeatures(partial);
		setBusinessHoursOpen(false);
	};

	const handleSave = async () => {
		// Validate + clean each configuration (mirrors the legacy onSave).
		const result: Api.IEmailProviderConfiguration[] = [];
		for (const key of Object.keys(configs)) {
			const config: IEmailConfigDraft = JSON.parse(JSON.stringify(configs[key]));
			if (!isValidDomain(config.domain ?? '')) {
				toast.error('Domain required for every configuration');
				return;
			}
			if (config.emailProvider === 'Yahoo' || config.emailProvider === 'Aol' || config.emailProvider === 'Imap') {
				if (!isEmailProviderConfigured(config)) {
					toast.error('IMAP and SMTP host/port are required for this provider');
					return;
				}
				config.exchange = undefined;
			} else if (config.emailProvider === 'Exchange') {
				const url = config.exchange?.webServicesUrl ?? '';
				if (!isEmailProviderConfigured(config) || !/^https?:/i.test(url) || !url.endsWith('/ews/Exchange.asmx')) {
					toast.error('Exchange URL must start with http(s) and end with /ews/Exchange.asmx');
					return;
				}
				if (url.includes('/owa/ews/')) {
					config.exchange = { ...config.exchange, webServicesUrl: url.replace('owa/ews', 'ews') };
				}
				config.imap = undefined;
			} else {
				config.imap = undefined;
				config.exchange = undefined;
			}
			const { key: _key, ...clean } = config;
			result.push(clean);
		}

		let emailDomain: string;
		if (forceMultipleDomains) {
			emailDomain = NONE_DOMAIN;
		} else if (result.length === 0) {
			toast.error('At least one email configuration required to save the account');
			return;
		} else {
			emailDomain = result.length === 1 ? `@${result[0].domain}` : NONE_DOMAIN;
		}

		const sendEmail = account.features?.sendEmail;
		// Compare against the effective stored set (absent → the standard default) so opening and saving
		// an untouched account never writes an explicit holiday list.
		const holidaysChanged = !holidaySetsEqual(sendEmail?.observedHolidays ?? STANDARD_HOLIDAYS, observedHolidays);
		const featuresChanged =
			(sendEmail?.observeSendIntervals ?? false) !== isBusinessHoursEnabled ||
			(sendEmail?.observeHolidays ?? true) !== isObserveHolidaysEnabled ||
			holidaysChanged;

		try {
			if (featuresChanged) {
				await persistSendEmailFeatures({
					observeSendIntervals: isBusinessHoursEnabled,
					observeHolidays: isObserveHolidaysEnabled,
					observedHolidays,
				});
			}
			const updatedAccount: Api.IAccount = {
				...account,
				emailDomain,
				emailProviderConfigurations: result,
				features: featuresChanged
					? {
							...account.features,
							sendEmail: {
								...(account.features?.sendEmail ?? {}),
								observeSendIntervals: isBusinessHoursEnabled,
								observeHolidays: isObserveHolidaysEnabled,
								observedHolidays,
							} as Api.ISendEmailFeatures,
						}
					: account.features,
			};
			await updateAccount.mutateAsync({ account: updatedAccount });
			invalidateAccountAdminQuery();
			onClose();
		} catch {
			toast.error('Failed to save email configuration. Please try again.');
		}
	};

	const textingUsesEmailHolidays = !account.features?.texting?.sendIntervals;
	const configuredEmailDomain = account.features?.sendEmail?.domainName;
	const configKeys = Object.keys(configs);

	return (
		<>
			<Dialog open={open} onOpenChange={next => !next && onClose()} dismissOnOutsideClick={false}>
				<DialogPopup styles={[s.popup]}>
					<DialogTitle>Configure Email</DialogTitle>
					<DialogBody styles={[s.body]}>
						<div className={css(s.topRow)}>
							<label className={css(s.checkboxRow)}>
								<Checkbox
									checked={forceMultipleDomains}
									onCheckedChange={checked => setForceMultipleDomains(checked === true)}
								/>
								<span>Allow Multiple Domains</span>
							</label>
							<Button variant='secondary' size='sm' onClick={handleAdd}>
								Add Configuration
							</Button>
						</div>

						<div className={css(s.configList)}>
							{configKeys.map(key => (
								<EmailConfigurationRow
									key={key}
									configuration={configs[key]}
									accountId={account.id}
									canUseLevDomain={canSupportLevel3}
									configuredSendDomain={configuredEmailDomain}
									onChange={next => handleRowChange(key, next)}
									onDelete={() => handleDelete(key)}
								/>
							))}
						</div>

						<div className={css(s.featuresGrid)}>
							<div className={css(s.featureColumn)}>
								<div className={css(s.featureHeader)}>Business Hours</div>
								{isBusinessHoursEnabled && (
									<Button variant='link' styles={[s.link]} onClick={() => setBusinessHoursOpen(true)}>
										Manage
									</Button>
								)}
								<label className={css(s.checkboxRow)}>
									<Checkbox
										checked={isBusinessHoursEnabled}
										onCheckedChange={checked => setIsBusinessHoursEnabled(checked === true)}
									/>
									<span>Enable Business Hours</span>
								</label>
							</div>

							<div className={css(s.featureColumn)}>
								<div className={css(s.featureHeader)}>
									Holidays
									<span className={css(s.infoTrigger)} title={HOLIDAY_INFO(textingUsesEmailHolidays)}>
										<InfoIcon />
									</span>
								</div>
								<label className={css(s.checkboxRow)}>
									<Checkbox
										id='observe-holidays'
										checked={isObserveHolidaysEnabled}
										onCheckedChange={checked => setIsObserveHolidaysEnabled(checked === true)}
									/>
									<span>
										{textingUsesEmailHolidays ? 'Pause emails and texts on holidays' : 'Pause emails on holidays'}
									</span>
								</label>
								<div className={css(s.holidayList)}>
									{HOLIDAY_ROWS.map(row => (
										<div key={row.id} className={css(s.holidayRow, row.indented ? s.holidayRowIndented : null)}>
											<Checkbox
												id={`observe-holiday-${row.id}`}
												checked={observedHolidays.includes(row.id)}
												disabled={!isObserveHolidaysEnabled}
												onCheckedChange={checked => handleToggleHoliday(row.id, checked === true)}
											/>
											<label htmlFor={`observe-holiday-${row.id}`} className={css(s.holidayLabel)}>
												{row.label}
											</label>
										</div>
									))}
								</div>
							</div>

							{canSupportLevel3 && (
								<div className={css(s.featureColumn)}>
									<div className={css(s.featureHeader)}>
										Email Domain
										<span className={css(s.infoTrigger)} title={EMAIL_DOMAIN_INFO}>
											<InfoIcon />
										</span>
									</div>
									{configuredEmailDomain ? (
										<span className={css(s.domainText)}>{configuredEmailDomain}</span>
									) : (
										<Button variant='link' styles={[s.link]} onClick={() => setEmailDomainOpen(true)}>
											Request New Email Domain
										</Button>
									)}
								</div>
							)}
						</div>
					</DialogBody>
					<DialogFooter>
						<Button variant='secondary' onClick={onClose} disabled={isSaving}>
							Cancel
						</Button>
						<Button onClick={handleSave} isLoading={isSaving}>
							Save
						</Button>
					</DialogFooter>
					{isSaving && (
						<div className={css(s.spinnerOverlay)}>
							<LoadingSpinner type='large' />
						</div>
					)}
				</DialogPopup>
			</Dialog>

			<BusinessHoursDialog
				open={businessHoursOpen}
				sendEmailFeatures={account.features?.sendEmail}
				isSaving={updateSendEmailFeatures.isPending}
				onClose={() => setBusinessHoursOpen(false)}
				onSave={handleBusinessHoursSave}
			/>

			<EmailDomainRequestDialog
				open={emailDomainOpen}
				impersonationContext={{ account: { id: account.id } }}
				features={account.features}
				onClose={() => setEmailDomainOpen(false)}
				onDomainRegistered={() => invalidateAccountAdminQuery()}
			/>
		</>
	);
};
